import { useState, useEffect } from "react";
import { api, endImpersonation } from "./api.js";
import { identifyPosthogUser } from "./analytics.js";
import App, { AuthGate } from "./pipeline-code.jsx";
import AdminDashboard from "./admin-dashboard.jsx";

const ROLES = [
  { key: "client", label: "Client Workspace", icon: "🏢", desc: "Lead gen, CRM, campaigns, AI tools", color: "#c4f04d" },
  { key: "admin", label: "Platform Admin", icon: "🛡️", desc: "All orgs, agencies, billing, system", color: "#4d9ef0" },
];

// Inner layout that runs only after AuthGate has confirmed authentication
function AuthenticatedLayout() {
  const [role, setRole] = useState("client");
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(null);
  const [impersonation, setImpersonation] = useState(null); // { email } when active

  // Check user's actual role from JWT — runs only when this component mounts (after auth)
  useEffect(() => {
    async function checkRole() {
      try {
        const result = await api.me();
        if (result?.user) {
          const userRoleFromDB = result.user.role || 'org_member';
          setUserRole(userRoleFromDB);
          if (userRoleFromDB === 'platform_admin') setRole('admin');
          identifyPosthogUser(result.user);
        }
        if (result?.impersonation?.active) {
          setImpersonation({ email: result.user?.email || 'user', name: result.user?.name || null });
        }
      } catch (err) {
        console.error("Failed to check user role:", err);
      } finally {
        setLoading(false);
        // Load credit balance
        api.billing.credits().then(d => { if (d?.balance !== undefined) setCredits(d.balance); }).catch(() => {});
      }
    }
    checkRole();
    // Refresh credits every 30s
    const creditsInterval = setInterval(() => {
      api.billing.credits().then(d => { if (d?.balance !== undefined) setCredits(d.balance); }).catch(() => {});
    }, 30000);
    return () => clearInterval(creditsInterval);
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#0a0a0f", display: "flex", alignItems: "center", justifyContent: "center", color: "#7a7a8e", fontFamily: "'DM Sans', sans-serif" }}>
        Loading...
      </div>
    );
  }

  const availableRoles = userRole === 'platform_admin' ? ROLES : ROLES.filter(r => r.key === 'client');

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: "#0a0a0f" }}>
        {/* Impersonation banner — shown whenever the active token carries an
            `imp` claim. Exit restores the admin's own token and reloads. */}
        {impersonation && (
          <div style={{
            display: "flex", alignItems: "center", gap: 10, padding: "8px 16px",
            background: "#7a2e00", borderBottom: "1px solid #a34400",
            flexShrink: 0, zIndex: 200,
          }}>
            <span style={{ fontSize: 13 }}>🎭</span>
            <span style={{ fontSize: 12, color: "#ffe8d6", fontFamily: "'DM Sans', sans-serif", fontWeight: 600 }}>
              Impersonating {impersonation.name ? `${impersonation.name} · ` : ""}{impersonation.email}
            </span>
            <span style={{ fontSize: 10, color: "#ffcba8" }}>Session ends in ≤30 min · logged to audit trail</span>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => { endImpersonation(); window.location.reload(); }}
              style={{
                padding: "5px 14px", borderRadius: 6, background: "#ffffff",
                border: "none", color: "#7a2e00", fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10, fontWeight: 700, cursor: "pointer",
              }}
            >
              Exit impersonation
            </button>
          </div>
        )}
        {/* Role Switcher Bar (only show if user is admin) */}
        {availableRoles.length > 1 && (
          <div style={{ 
            display: "flex", alignItems: "center", gap: 8, padding: "8px 16px",
            background: "#06060a", borderBottom: "1px solid #1e1e2e",
            flexShrink: 0, zIndex: 100
          }}>
            <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#4d9ef0", marginRight: 8, letterSpacing: "0.05em" }}>CONSULTIX</span>
            <span style={{ fontSize: 9, color: "#4a4a5e", marginRight: 12 }}>PLATFORM</span>
            <div style={{ display: "flex", gap: 4 }}>
              {availableRoles.map(r => (
                <button 
                  key={r.key} 
                  onClick={() => setRole(r.key)}
                  style={{
                    padding: "6px 14px", borderRadius: 6, cursor: "pointer",
                    background: role === r.key ? r.color + "15" : "transparent",
                    border: `1px solid ${role === r.key ? r.color + "44" : "transparent"}`,
                    color: role === r.key ? r.color : "#7a7a8e",
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600,
                    transition: "all 0.15s", display: "flex", alignItems: "center", gap: 6,
                  }}
                >
                  <span style={{ fontSize: 12 }}>{r.icon}</span>
                  {r.label}
                </button>
              ))}
            </div>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 9, color: "#4a4a5e", fontFamily: "'JetBrains Mono', monospace" }}>
              {ROLES.find(r => r.key === role)?.desc}
            </span>
            <button
              onClick={() => { window.location.hash = '#account-billing'; }}
              style={{
                marginLeft: 12, padding: "5px 14px", borderRadius: 6,
                background: "transparent", border: "1px solid #c4f04d33",
                color: "#c4f04d", fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10, fontWeight: 600, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(196,240,77,0.08)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            >
              🪙 {credits !== null ? credits.toLocaleString() : '—'} credits
            </button>
            <button
              onClick={() => { api.clearToken(); window.location.reload(); }}
              style={{
                marginLeft: 8, padding: "5px 14px", borderRadius: 6,
                background: "transparent", border: "1px solid #f04d4d33",
                color: "#f04d4d", fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10, fontWeight: 600, cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(240,77,77,0.08)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            >
              Log Out
            </button>
          </div>
        )}
        
        {/* Dashboard Content */}
        <div style={{ flex: 1, overflow: "hidden" }}>
          {role === "admin" && <AdminDashboard />}
          {role === "client" && <App userRole={userRole} />}
        </div>
      </div>
  );
}

export default function AppWrapper() {
  return (
    <AuthGate>
      <AuthenticatedLayout />
    </AuthGate>
  );
}
